// src/api.ts (CRA)
import axios from 'axios';

// URL base desde .env.* (CRA inyecta process.env.REACT_APP_*)
const API_BASE = process.env.REACT_APP_API_URL || '';

// Sin REACT_APP_API_URL se usa el mismo dominio (/api), que es la
// configuración recomendada para el despliegue PHP en DirectAdmin.

// Instancia de axios
const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // para cookies (sesión/JWT)
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // en Axios v1, headers puede estar undefined y el tipo es estricto
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      } as any;
    }

    if (process.env.NODE_ENV !== 'production') {
      const method = (config.method ?? 'get').toUpperCase();
      const url = `${config.baseURL ?? ''}${config.url ?? ''}`;
      // eslint-disable-next-line no-console
      console.log('[api→]', method, url);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Algunos hosting compartidos bloquean los métodos PUT, PATCH y DELETE en el
// servidor web y responden 405, 406 o 501 antes de que la petición llegue a la
// API. Cuando eso ocurre se reenvía la misma petición como POST, indicando el
// método real en una cabecera que el backend interpreta. El primer bloqueo
// activa el modo alternativo para el resto de la sesión.
const METODOS_REESCRIBIBLES = ['put', 'patch', 'delete'];
const ESTADOS_DE_BLOQUEO = [405, 406, 501];
let usarMetodoAlternativo = false;

api.interceptors.request.use((config) => {
  const metodo = (config.method ?? 'get').toLowerCase();
  if (usarMetodoAlternativo && METODOS_REESCRIBIBLES.includes(metodo)) {
    config.headers = {
      ...(config.headers || {}),
      'X-HTTP-Method-Override': metodo.toUpperCase(),
    } as any;
    config.method = 'post';
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV !== 'production') {
      const url = `${response.config.baseURL ?? ''}${response.config.url ?? ''}`;
      // eslint-disable-next-line no-console
      console.log('[←api]', response.status, url);
    }
    return response;
  },
  (error) => {
    const config: any = error.config;
    const estado = error.response?.status;
    const metodo = (config?.method ?? '').toLowerCase();

    if (
      config &&
      !config.__reintentoMetodo &&
      ESTADOS_DE_BLOQUEO.includes(estado) &&
      METODOS_REESCRIBIBLES.includes(metodo)
    ) {
      usarMetodoAlternativo = true;
      config.__reintentoMetodo = true;
      config.headers = {
        ...(config.headers || {}),
        'X-HTTP-Method-Override': metodo.toUpperCase(),
      };
      config.method = 'post';
      return api.request(config);
    }

    return Promise.reject(error);
  }
);

export default api;

// ------- Funciones existentes -------
export const getDashboardStats = async () => {
  try {
    const { data: analisis = [] } = await api.get('/analisis/search');
    const solicitados = analisis.length;
    const pendientes = analisis.filter((a: any) => a.estado === 0).length;
    const completados = analisis.filter((a: any) => a.estado === 1).length;
    const enProceso = 0;

    return { solicitados, pendientes, enProceso, completados };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error al obtener estadísticas del dashboard:', error);
    return { solicitados: 0, pendientes: 0, enProceso: 0, completados: 0 };
  }
};

export const getProximosVencimientos = async () => {
  try {
    const { data: analisis = [] } = await api.get('/analisis/search');
    const ahora = new Date();
    return analisis
      .filter((a: any) => {
        if (a.estado === 1) return false;
        const fecha = new Date(a.fecha_solicitud);
        const dias = Math.floor((ahora.getTime() - fecha.getTime()) / (1000 * 60 * 60 * 24));
        return dias >= 5;
      })
      .sort(
        (a: any, b: any) =>
          new Date(a.fecha_solicitud).getTime() - new Date(b.fecha_solicitud).getTime()
      )
      .slice(0, 5);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error al obtener próximos vencimientos:', error);
    return [];
  }
};
